<template>
  <section
    v-if="attributes"
    :class="$style.root"
  >
    <div :class="$style.content">
      <pagesEventsSlugComponent :data="data" />
    </div>

    <LayoutPrevNextPageComponent
      :class="$style.prevNext"
      :prev="prevData"
      :next="nextData"
    />
  </section>
  <SharedLocaleUnavailableComponent
    v-else-if="fallbackAttributes"
    :slug="fallbackAttributes?.slug"
    :title="fallbackAttributes?.title"
  />
  <SharedErrorComponent v-else-if="error" :error="error" />
</template>

<script lang="ts" setup>
const route = useRoute()
const getAttributes = baseStrapiGetAttributes

// 1
useHead({
  htmlAttrs: { 'data-page-type': 'events-slug' }
})

// 2
const { data, error } = await useAsyncData(`events-slug-${route.params.slug}`, () =>
  fetchQueryPath( queryEventEntryBySlug(baseStrapiGetSlugQueryObjectFromRoute()) )
)

const attributes = baseStrapiGetAttributes(data)
const defaultOgImageUrl = baseStrapiGetAbsoluteFileUrl(attributes?.coverImage?.data?.attributes?.url, attributes?.coverImage?.data?.provider)
basePageSetHead(data, defaultOgImageUrl)

// 2b. Not every entry is translated. Rather than rendering an empty page, look
// the same slug up in the default locale so the visitor gets told the
// translation is missing and can jump to the version that does exist.
const { locales, defaultLocale } = useI18n()
const defaultIso = locales.value?.find(locale => locale?.code === defaultLocale)?.iso
const needsFallback = !attributes && baseI18nGetActiveIso() !== defaultIso

const { data: fallback } = await useAsyncData(`events-slug-fallback-${route.params.slug}`, () =>
  needsFallback
    ? fetchQueryPath( queryEventEntryTitleBySlug({ ...baseStrapiGetSlugQueryObjectFromRoute(), locale: defaultIso }) )
    : Promise.resolve(null)
)
const fallbackAttributes = baseStrapiGetAttributes(fallback)

// An untranslated entry is a stub, not content — keep it out of the index.
if (fallbackAttributes) {
  useHead({
    title: fallbackAttributes?.title,
    meta: [{ name: 'robots', content: 'noindex, follow' }]
  })
}

// 3
const prevQueryOptions = baseStrapiGetPrevNextQueryObject(getAttributes(data)?.sortKeyAsc, '$lt', 'desc')
const { data: prev } = await useAsyncData(`events-slug-prev-${route.params.slug}`,() =>
  fetchQueryPath( queryEventEntryPrevNextBySlug(prevQueryOptions) )
)
const prevData = {...getAttributes(prev), query: { year: dayJs(getAttributes(prev)?.sortKeyAsc)?.format('YYYY'), month: dayJs(getAttributes(prev)?.sortKeyAsc)?.format('M') }}

const nextQueryOptions = baseStrapiGetPrevNextQueryObject(getAttributes(data)?.sortKeyAsc, '$gt', 'asc')
const { data: next } = await useAsyncData(() =>
  fetchQueryPath( queryEventEntryPrevNextBySlug(nextQueryOptions) )
)
const nextData = { ...getAttributes(next), query: { year: dayJs(getAttributes(next)?.sortKeyAsc)?.format('YYYY'), month: dayJs(getAttributes(next)?.sortKeyAsc)?.format('M') } }
</script>

<style module>
.root {
  composes: container--modal from global;

  margin-bottom: calc(var(--unit--vertical) * 2);
}

.prevNext {
  margin-top: var(--unit--vertical);
}
</style>
